import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Mobile WebTrader Layout
 * Bottom navigation with 4 tabs: Home, Markets, Positions, Funds
 * Each tab renders a different full-screen view
 */
import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobileAccountSummary } from './MobileAccountSummary';
import '../styles/mt5-terminal.css';
import '../styles/mt5-mobile.css';
import { orderIntent } from '../utils/orderIntent';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import { useTraderAuth } from '../context/TraderAuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { scopedQuote } from '../utils/quotes';
import { isBinancePair } from '../utils/binance';
import { useMarketClock } from '../hooks/useBinanceHistory';
import { watchLiveRefresh } from '../../hooks/liveRefresh';
import { 
  Home, BarChart3, Layers, Wallet, Bell, ChevronDown, Search, Star, ChevronRight, ChevronLeft, Clock,
  X, Banknote, ArrowUpCircle, ArrowDownCircle, Calendar, HelpCircle, Settings, Upload, 
  Sun, Moon, LogOut, ArrowUpDown, CandlestickChart, History, Menu, Smartphone
} from 'lucide-react';
import InstrumentIcon from './InstrumentIcon';
import {AccountSwitcher,CustomerNotifications} from './CustomerAccountTools';
import {NotificationBadge} from '../notifications/NotificationContext';
import {AccessApprovalNotice} from './AccessApprovalNotice';
import {TraderConnection} from '../../components/connection/TraderConnection';
import {DepositWorkspace} from '../deposits/DepositWorkspace';
import {WithdrawalWorkspace} from '../withdrawals/WithdrawalWorkspace';
import NewOrderModal from './NewOrderModal';
import MobileInstrumentDetail from './MobileInstrumentDetail';
import PWAInstallModal from './PWAInstallModal';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { MobileCalendarView, MobileSettingsView } from './MobileViews';
import {CustomerMobileDocuments as MobileDocumentsView} from './CustomerMobileDocuments';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from '../../components/ui/alert-dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Trading hours configuration - MUST MATCH backend trading_sessions.py MARKET_HOURS
const TRADING_HOURS = {
  forex: { hours: 'Mon-Fri', fullDesc: 'Monday 01:00 - Friday 23:59 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(); return d >= 1 && d <= 5 && !(d === 1 && h < 1); } },
  crypto: { hours: '24/7', fullDesc: 'Always open', isOpen: () => true },
  stocks: { hours: '14:30-21:00 UTC', fullDesc: 'Mon-Fri 14:30 - 21:00 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(), m = new Date().getUTCMinutes(); return d >= 1 && d <= 5 && (h + m/60) >= 14.5 && h < 21; } },
  stocks_us: { hours: '14:30-21:00 UTC', fullDesc: 'Mon-Fri 14:30 - 21:00 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(), m = new Date().getUTCMinutes(); return d >= 1 && d <= 5 && (h + m/60) >= 14.5 && h < 21; } },
  stocks_bist: { hours: '07:00-15:00 UTC', fullDesc: 'Mon-Fri 07:00 - 15:00 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(); return d >= 1 && d <= 5 && h >= 7 && h < 15; } },
  bist: { hours: '07:00-15:00 UTC', fullDesc: 'Mon-Fri 07:00 - 15:00 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(); return d >= 1 && d <= 5 && h >= 7 && h < 15; } },
  commodities: { hours: 'Sun 23:00 - Fri 22:00', fullDesc: 'Sunday 23:00 UTC - Friday 22:00 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(); return !(d === 6 || (d === 0 && h < 23) || (d === 5 && h >= 22)); } },
  indices: { hours: 'Sun 23:00 - Fri 22:00', fullDesc: 'Sunday 23:00 UTC - Friday 22:00 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(); return !(d === 6 || (d === 0 && h < 23) || (d === 5 && h >= 22)); } },
};

// Mobile Home View
const MobileHomeView = ({ 
  instruments, 
  favorites, 
  toggleFavorite, 
  onSelectInstrument,
  onInstrumentClick,
  formatPrice,
  user,
  isDark,
  t
}) => {
  const tx = useTraderText();
  const { prices, replaceSubscriptions } = useWebSocket();
  const [activeTab, setActiveTab] = useState('popular');
  const tabs = [
    { id: 'popular', label: 'Most Popular' },
    { id: 'open', label: 'Open now' },
    { id: 'watchlist', label: 'My Watchlist' },
  ];

  const rawFilteredInstruments = useMemo(() => {
    if (activeTab === 'watchlist') {
      return instruments.filter(i => favorites.includes(i.symbol));
    }
    if (activeTab === 'open') {
      // Show forex (24/5) and crypto (24/7) which are typically open
      return instruments.filter(i => i.category === 'forex' || i.category === 'crypto');
    }
    // Popular - show major forex, gold, bitcoin first
    const popularSymbols = ['EUR/USD', 'XAU/USD', 'BTC/USD', 'GBP/USD', 'USD/JPY', 'XAG/USD', 'ETH/USD', 'US100'];
    return [...instruments].sort((a, b) => {
      const aIdx = popularSymbols.indexOf(a.symbol);
      const bIdx = popularSymbols.indexOf(b.symbol);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return 0;
    }).slice(0, 20);
  }, [instruments, activeTab, favorites]);

  // Only apply live quotes to the visible slice
  const visibleInstruments = useMemo(() => {
    return rawFilteredInstruments.map(inst => {
      const live = scopedQuote(inst, prices[inst.symbol]);
      return live ? { ...inst, bid: live.bid, ask: live.ask, change: live.change_pct || inst.change } : inst;
    });
  }, [rawFilteredInstruments, prices]);

  useEffect(() => {
    const syms = visibleInstruments.map(i => i.symbol).filter(Boolean);
    if (syms.length > 0 && typeof replaceSubscriptions === 'function') {
      replaceSubscriptions(syms);
    }
  }, [visibleInstruments, replaceSubscriptions]);

  const currency = user?.currency || 'TRY';
  const symbol = currency === 'TRY' ? '₺' : '$';
  const balance = user?.balance || 0;

  return (
    <div className="flex flex-col h-full">
      {/* Greeting and Balance */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Hi, ")}<span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {user?.full_name?.split(' ')[0] || 'Trader'}
            </span>
          </p>
        </div>
        <div className="text-right">
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Your balance")}</p>
          <p className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {symbol}{balance.toLocaleString(tx('en-US'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className={`mx-4 p-1 rounded-full flex ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`mobile-home-tab-${tab.id}`}
            className={`flex-1 py-2 px-3 rounded-full text-xs font-medium transition-colors ${
              activeTab === tab.id
                ? isDark
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-900 shadow'
                : isDark
                  ? 'text-gray-400'
                  : 'text-gray-500'
            }`}
          >
            {tx(tab.label)}
          </button>
        ))}
      </div>

      {/* Market Header */}
      <div className="mt5-mobile-quote-heading" data-testid="mobile-home-quote-columns"><span>{tx('Symbol')}</span><span>{tx('Bid')}</span><span>{tx('Ask')}</span><span>{tx('Day %')}</span></div>

      {/* Instrument List */}
      <div className="flex-1 overflow-y-auto">
        {visibleInstruments.map((inst) => (
          <MobileInstrumentRow
            key={inst.id || inst.symbol}
            inst={inst}
            isDark={isDark}
            isFavorite={favorites.includes(inst.symbol)}
            toggleFavorite={toggleFavorite}
            onSelect={() => onSelectInstrument(inst)}
            onTrade={() => onInstrumentClick(inst)}
            formatPrice={formatPrice}
          />
        ))}
      </div>
    </div>
  );
};

// Mobile Markets View (Optimized for visible-only subscriptions and lazy rendering)
const MobileMarketsView = ({ 
  instruments, 
  favorites, 
  toggleFavorite,
  onSelectInstrument,
  onInstrumentClick,
  formatPrice,
  isDark,
  t,
  selectedSymbol,
  positionSymbols = [],
}) => {
  const tx = useTraderText();
  const { prices, replaceSubscriptions } = useWebSocket();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [visibleLimit, setVisibleLimit] = useState(25);

  useEffect(() => {
    setVisibleLimit(25);
  }, [search, category]);

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'forex', label: tx('Currency Pairs') },
    { id: 'crypto', label: 'Crypto' },
    { id: 'indices', label: 'Indices' },
    { id: 'commodities', label: 'Commodities' },
    { id: 'bist', label: 'Turkish Equities' },
  ];

  const rawFilteredInstruments = useMemo(() => {
    const s = search.toLowerCase().trim();
    return instruments.filter(inst => {
      const matchesSearch = !s ||
                            inst.symbol?.toLowerCase().includes(s) ||
                            inst.name?.toLowerCase().includes(s);
      const matchesCategory = category === 'all' || 
                             (category === 'bist' && inst.tradingview_symbol?.startsWith('BIST:')) ||
                             inst.category === category;
      return matchesSearch && matchesCategory;
    });
  }, [instruments, search, category]);

  // Only apply live quotes to the visible slice
  const visibleInstruments = useMemo(() => {
    return rawFilteredInstruments.slice(0, visibleLimit).map(inst => {
      const live = scopedQuote(inst, prices[inst.symbol]);
      if (live) {
        return {
          ...inst,
          bid: live.bid,
          ask: live.ask,
          session_status: live.session_status,
          quote_source: live.source,
          quote_available: live.quote_available,
          is_live_market: live.is_live_market,
          change: live.change_pct || inst.change,
        };
      }
      return !inst.is_virtual && isBinancePair(inst.symbol)
        ? { ...inst, bid: 0, ask: 0, price: 0, quote_source: 'binance', is_live_market: true, quote_available: false }
        : inst;
    });
  }, [rawFilteredInstruments, visibleLimit, prices]);

  // Subscribe strictly to visible instruments in the menu + selected instrument + open positions
  useEffect(() => {
    const symbols = visibleInstruments.map(i => i.symbol).filter(Boolean);
    if (selectedSymbol && !symbols.includes(selectedSymbol)) {
      symbols.push(selectedSymbol);
    }
    if (Array.isArray(positionSymbols)) {
      positionSymbols.forEach(s => {
        if (s && !symbols.includes(s)) symbols.push(s);
      });
    }
    if (symbols.length > 0 && typeof replaceSubscriptions === 'function') {
      replaceSubscriptions(symbols);
    }
  }, [visibleInstruments, selectedSymbol, positionSymbols, replaceSubscriptions]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 250) {
      setVisibleLimit(prev => Math.min(rawFilteredInstruments.length, prev + 25));
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search and Filter */}
      <div className="p-4 space-y-3">
        <div className="flex gap-2">
          <div className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-2.5 rounded-full border ${
            isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <Search size={18} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tx("Search")}
              data-testid="mobile-market-search"
              className={`flex-1 w-full min-w-0 bg-transparent text-sm outline-none ${
                isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
              }`}
            />
          </div>
          <select
            value={category}
            data-testid="mobile-market-category"
            onChange={(e) => setCategory(e.target.value)}
            className={`w-32 max-w-[40%] min-w-0 shrink-0 px-3 py-2.5 rounded-full border text-sm ${
              isDark 
                ? 'bg-gray-800/50 border-gray-700 text-white' 
                : 'bg-white border-gray-200 text-gray-900'
            }`}
          >
            {categories.map(c => (
              <option key={c.id} value={c.id}>{tx(c.label)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Market Header */}
      <div className="mt5-mobile-quote-heading" data-testid="mobile-market-quote-columns"><span>{tx('Symbol')}</span><span>{tx('Bid')}</span><span>{tx('Ask')}</span><span>{tx('Day %')}</span></div>

      {/* Instrument List with Infinite Scroll */}
      <div className="flex-1 overflow-y-auto" onScroll={handleScroll}>
        {visibleInstruments.map((inst) => (
          <MobileInstrumentRow
            key={inst.id || inst.symbol}
            inst={inst}
            isDark={isDark}
            isFavorite={favorites.includes(inst.symbol)}
            toggleFavorite={toggleFavorite}
            onSelect={() => onSelectInstrument(inst)}
            onTrade={() => onInstrumentClick(inst)}
            formatPrice={formatPrice}
          />
        ))}
      </div>
    </div>
  );
};

// Mobile Positions View
const MobilePositionsView = ({ user, positions, isDark, t, historyOnly = false }) => {
  const tx = useTraderText();
  const [activeTab, setActiveTab] = useState(historyOnly ? 'closed' : 'open');
  const [confirming,setConfirming]=useState(null),[processing,setProcessing]=useState(false),[actionError,setActionError]=useState('');
  const submitAction=async()=>{
    if(!confirming||processing)return;
    setProcessing(true);setActionError('');
    try{
      const pending=confirming.status==='pending';
      const response=await fetch(`${API_BASE}/api/trader/${pending?`orders/${confirming.id}`:`positions/${confirming.id}/close`}`,{method:pending?'DELETE':'POST',headers:{Authorization:`Bearer ${localStorage.getItem('trader_token')}`,'Content-Type':'application/json'},body:pending?undefined:JSON.stringify({})});
      const result=await response.json();
      if(!response.ok)throw new Error(typeof result.detail==='string'?result.detail:'The trade could not be updated.');
      window.dispatchEvent(new Event('trader-data-changed'));toast.success(tx(pending?'Order cancelled':'Position closed'));setConfirming(null);
    }catch(error){setActionError(error.message);}finally{setProcessing(false);}
  };
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('time');

  const currency = user?.currency || 'TRY';
  const symbol = currency === 'TRY' ? '₺' : '$';
  const balance = user?.balance || 0;
  const credit = user?.credit || 0;
  const equity = user?.equity ?? balance + credit;
  const usedMargin = user?.used_margin ?? 0;
  const freeMargin = user?.free_margin ?? equity - usedMargin;

  const tabs = (historyOnly ? [
    { id: 'closed', label: 'Closed Positions' },
  ] : [
    { id: 'open', label: 'Open', count: positions?.filter(p => p.status === 'open').length || 0 },
    { id: 'pending', label: 'Pending' },
  ]);

  const filteredPositions = useMemo(() => {
    let result = positions || [];
    if (activeTab === 'open') result = result.filter(p => p.status === 'open');
    else if (activeTab === 'pending') result = result.filter(p => p.status === 'pending');
    else result = result.filter(p => p.status === 'closed');
    
    if (search) {
      result = result.filter(p => p.symbol?.toLowerCase().includes(search.toLowerCase()));
    }
    return result;
  }, [positions, activeTab, search]);

  return (
    <div className="mt5-mobile-positions flex flex-col h-full" data-testid={historyOnly ? 'mobile-history-view' : 'mobile-trade-view'}>
      {!historyOnly && <MobileAccountSummary currency={currency} balance={balance} credit={credit} equity={equity} freeMargin={freeMargin} usedMargin={usedMargin} openPL={user?.open_pl ?? 0}/>}

      {/* Tabs */}
      <div className={`mx-4 mt-4 p-1 rounded-full flex ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`mobile-positions-tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            className={`flex-1 py-2 px-3 rounded-full text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
              activeTab === tab.id
                ? 'bg-cyan-500 text-white'
                : isDark
                  ? 'text-gray-400'
                  : 'text-gray-500'
            }`}
          >
            {tx(tab.label)}
            {tab.count !== undefined && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                activeTab === tab.id ? 'bg-white/20' : isDark ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search and Sort */}
      <div className="px-4 py-3 flex gap-2">
        <div className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-full border ${
          isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <Search size={16} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tx("Search")}
            data-testid="mobile-position-search"
            className={`flex-1 bg-transparent text-sm outline-none ${
              isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
            }`}
          />
        </div>
        <div className="text-right">
          <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("Sort by")}</p>
          <select
            value={sortBy}
            data-testid="mobile-position-sort"
            onChange={(e) => setSortBy(e.target.value)}
            className={`bg-transparent text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}
          >
            <option value="time">{tx("Open time")}</option>
            <option value="symbol">{tx("Symbol")}</option>
            <option value="pnl">{tx("P/L")}</option>
          </select>
        </div>
      </div>

      {/* Positions List */}
      <div className="flex-1 overflow-y-auto px-4" style={{ WebkitOverflowScrolling: 'touch' }}>
        {filteredPositions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <div className="w-32 h-32 mb-4 flex items-center justify-center">
              <svg viewBox="0 0 120 120" className="w-full h-full">
                <circle cx="60" cy="80" r="25" fill="#3B82F6" opacity="0.2"/>
                <rect x="40" y="30" width="40" height="50" rx="4" fill="#60A5FA"/>
                <rect x="50" y="40" width="20" height="4" rx="2" fill="white"/>
                <rect x="50" y="50" width="15" height="4" rx="2" fill="white"/>
                <circle cx="85" cy="45" r="15" fill="#F59E0B"/>
                <path d="M80 45 L85 50 L95 40" stroke="white" strokeWidth="3" fill="none"/>
              </svg>
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("No items to display")}</p>
          </div>
        ) : (
          filteredPositions.map((pos) => (
            <div 
              key={pos.id} 
              data-testid={`mobile-position-${pos.id}`}
              className={`py-3 px-4 mb-2 rounded-xl ${isDark ? 'bg-gray-800/50' : 'bg-white shadow-sm'}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{pos.symbol}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      (pos.side || pos.direction) === 'buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {tx((pos.side || pos.direction)?.toUpperCase())}
                    </span>
                  </div>
                  <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <span>{tx("Vol: ")}{pos.volume || pos.quantity}</span>
                    <span className="mx-2">|</span>
                    <span data-testid={`mobile-trade-price-${pos.id}`}>@ {Number(pos.open_price ?? pos.entry_price ?? pos.price ?? 0).toFixed(5)}</span>
                  </div>
                </div>
                <div className="text-right">{pos.status==='pending'?<span data-testid={`mobile-trade-status-${pos.id}`} className={`text-xs ${isDark?'text-gray-400':'text-gray-500'}`}>{tx("Pending ")}{tx(pos.type||pos.order_type)}</span>:<>
                  <p className={`font-bold text-lg ${(pos.pnl || pos.profit_loss || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {(pos.pnl || pos.profit_loss || 0) >= 0 ? '+' : ''}{symbol}{(pos.pnl || pos.profit_loss || 0).toFixed(2)}
                  </p>
                  <p className={`text-xs ${(pos.pnl || pos.profit_loss || 0) >= 0 ? 'text-green-500/70' : 'text-red-500/70'}`}>
                    {(pos.pnl_pct ?? 0).toFixed(2)}%
                  </p>
                  </>}
                </div>
              </div>
              {/* Close button - only for open positions */}
              {(pos.status === 'open'||pos.status==='pending') && (
                <button
                  onClick={() => {setActionError('');setConfirming(pos);}}
                  data-testid={`mobile-${pos.status==='pending'?'cancel':'close'}-${pos.id}`}
                  className={`mt-3 w-full py-2.5 rounded-lg text-sm font-medium transition-all active:scale-98 ${
                    isDark ? 'bg-gray-700 text-white active:bg-gray-600' : 'bg-gray-100 text-gray-900 active:bg-gray-200'
                  }`}
                >
                  {tx(pos.status==='pending'?'Cancel Order':'Close Position')}
                </button>
              )}
            </div>
          ))
        )}
      </div>
      <AlertDialog open={!!confirming} onOpenChange={open=>{if(!open&&!processing)setConfirming(null);}}><AlertDialogContent data-testid="mobile-trade-confirm" className={isDark?'bg-gray-900 text-white border-gray-700':''}><AlertDialogHeader><AlertDialogTitle data-testid="mobile-trade-confirm-title">{tx(confirming?.status==='pending'?'Cancel order':'Close position')} — {confirming?.symbol}</AlertDialogTitle><AlertDialogDescription>{tx(confirming?.status==='pending'?'This pending order will be cancelled.':'This position will close at the available server quote.')}</AlertDialogDescription></AlertDialogHeader>{actionError&&<p role="alert" data-testid="mobile-trade-confirm-error" className="text-red-500 text-sm">{tx(actionError)}</p>}<AlertDialogFooter><AlertDialogCancel data-testid="mobile-trade-confirm-cancel" disabled={processing}>{tx("Keep trade")}</AlertDialogCancel><AlertDialogAction data-testid="mobile-trade-confirm-submit" disabled={processing} onClick={event=>{event.preventDefault();submitAction();}}>{tx(processing?'Processing…':confirming?.status==='pending'?'Cancel order':'Close position')}</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
};

// Mobile Funds View
const MobileFundsView = ({ user, positions, isDark, t, onNavigate }) => {
  const tx = useTraderText();
  const currency = user?.currency || 'TRY';
  const symbol = currency === 'TRY' ? '₺' : '$';
  const balance = user?.balance || 0;
  const credit = user?.credit || 0;
  const openPnL = user?.open_pl ?? 0;
  const equity = user?.equity ?? balance + credit + openPnL;
  const usedMargin = user?.used_margin ?? 0;
  const freeMargin = user?.free_margin ?? equity - usedMargin;

  const exposureByCategory={forex:0,commodities:0,other:0};
  (positions||[]).filter(p=>p.status==='open').forEach(p=>{
    const category=Object.hasOwn(exposureByCategory,p.category)?p.category:'other';
    exposureByCategory[category]+=Math.abs((p.quantity??p.volume??0)*(p.contract_size??1)*(p.current_price??p.entry_price??0));
  });
  const totalExposure=Object.values(exposureByCategory).reduce((sum,value)=>sum+value,0);
  let allocationOffset=0;
  const allocations=[['forex','FX','#06b6d4'],['commodities','Commodities','#F59E0B'],['other','Other','#94a3b8']].map(([key,name,color])=>{
    const percent=totalExposure?exposureByCategory[key]/totalExposure*100:0;
    const item={name,color,percent,offset:allocationOffset};allocationOffset+=percent;return item;
  });

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Asset Allocation Header */}
      <div className="px-4 pt-4 flex items-center justify-between">
        <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Asset Allocation")}</h2>
      </div>

      {/* Donut Chart */}
      <div className="px-4 py-6 flex flex-col items-center">
        <div className="relative w-48 h-32">
          <svg viewBox="0 0 200 120" className="w-full h-full">
            <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke={isDark?'#374151':'#e5e7eb'} strokeWidth="16"/>
            {allocations.filter(a=>a.percent>0).map(a=><path key={a.name} d="M 20 100 A 80 80 0 0 1 180 100" pathLength="100" fill="none" stroke={a.color} strokeWidth="16" strokeDasharray={`${a.percent} ${100-a.percent}`} strokeDashoffset={-a.offset}/>)}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Open exposure")}</p>
            <p data-testid="mobile-funds-exposure" className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {symbol}{totalExposure.toLocaleString(tx('en-US'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex gap-4 mt-2">
          {allocations.map((a) => (
            <div key={a.name} className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: a.color }}></div>
              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {tx(a.name)} {a.percent.toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Account Balance */}
      <div className="px-4 pb-8">
        <h3 className={`text-base font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Account Balance")}</h3>
        
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Open P/L")}</span>
            <span data-testid="mobile-funds-open-pl" className={`font-medium ${openPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {symbol}{openPnL.toLocaleString(tx('en-US'), { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Balance")}</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {symbol}{balance.toLocaleString(tx('en-US'), { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Equity")}</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {symbol}{equity.toLocaleString(tx('en-US'), { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Used margin")}</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {symbol}{usedMargin.toLocaleString(tx('en-US'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Free margin")}</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {symbol}{freeMargin.toLocaleString(tx('en-US'), { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Instrument Row Component
const MobileInstrumentRow = memo(({ inst, isDark, isFavorite, toggleFavorite, onSelect, onTrade, formatPrice }) => {
  const tx = useTraderText();
  const marketStatus = TRADING_HOURS[inst.category] || TRADING_HOURS.forex;
  const isOpen = inst.is_virtual ? inst.session_status==='open' : marketStatus.isOpen();
  
  // Get mid price for display
  const midPrice = inst.bid && inst.ask ? (inst.bid + inst.ask) / 2 : inst.bid || inst.ask || 0;
  const change = inst.change || 0;
  const high = inst.high || (midPrice * 1.005);
  const low = inst.low || (midPrice * 0.995);
  
  return (
    <div
      onClick={onSelect}
      data-testid={`mobile-instrument-${inst.symbol?.replace('/', '-')}`}
      className="mt5-mobile-quote"
      role="button"
      tabIndex={0}
      onKeyDown={event => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onSelect(); } }}
    >
      <div className="mt5-mobile-quote-grid">
        <div className="mt5-mobile-symbol"><strong title={inst.symbol}>{inst.symbol}</strong>
          <button data-testid={`mobile-favorite-${inst.symbol?.replace('/', '-')}`} aria-label={tx('Favorite instrument')} aria-pressed={isFavorite} onClick={event => { event.stopPropagation(); toggleFavorite(inst.symbol, event); }}><Star size={12} className={isFavorite ? 'text-yellow-500 fill-yellow-500' : ''}/></button>
        </div>
        <button className="mt5-bid" data-testid={`mobile-quote-bid-${inst.symbol?.replace('/', '-')}`} onClick={event => { event.stopPropagation(); onTrade(); }}>{formatPrice(inst.bid, inst.symbol)}</button>
        <button className="mt5-ask" data-testid={`mobile-quote-ask-${inst.symbol?.replace('/', '-')}`} onClick={event => { event.stopPropagation(); onTrade(); }}>{formatPrice(inst.ask, inst.symbol)}</button>
        <span data-testid={`mobile-quote-change-${inst.symbol?.replace('/', '-')}`} className={change < 0 ? 'text-red-500' : 'text-green-500'}>{change > 0 ? '+' : ''}{change.toFixed(2)}%</span>
      </div>
      <div className="mt5-mobile-quote-meta"><span title={inst.name}>{inst.name || inst.symbol}</span><span>{tx('H: ')}{formatPrice(high, inst.symbol)} · {tx('L: ')}{formatPrice(low, inst.symbol)}</span></div>
    </div>
  );
});

// Main Mobile Layout Component
const MobileTraderLayout = () => {
  const tx = useTraderText();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTraderTheme();
  const { t, language, setLanguage } = useTraderLanguage();
  const { user, logout } = useTraderAuth();
  const { prices, positions: wsPositions, subscribe, replaceSubscriptions, isConnected, requestPositions } = useWebSocket();
  
  const [activeTab, setActiveTab] = useState('markets');
  const [instruments, setInstruments] = useState([]);
  const [favorites, setFavorites] = useState(['EUR/USD', 'XAU/USD', 'BTC/USD']);
  const [loading, setLoading] = useState(true);
  const quoteClock = useMarketClock(instruments.some(inst=>!inst.is_virtual&&isBinancePair(inst.symbol)));
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showCustomerNotifications, setShowCustomerNotifications] = useState(false);
  const [showInstrumentDetail, setShowInstrumentDetail] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [apiPositions, setApiPositions] = useState([]);
  
  // Views state for sidebar navigation
  const [currentView, setCurrentView] = useState(null); // null, 'deposit', 'withdraw', 'calendar', 'settings', 'documents'
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const { isInstallable, isInstalled, isIOS, promptInstall, showIOSModal, setShowIOSModal } = usePWAInstall();
  
  // Fetch positions from API (more reliable than WebSocket for initial load)
  useEffect(() => {
    let active=true;
    const fetchPositions = async () => {
      try {
        const token = localStorage.getItem('trader_token');
        if (!token) return;
        
        const [response,orders]=await Promise.all([fetch(`${API_BASE}/api/trader/positions?status=`,{headers:{Authorization:`Bearer ${token}`}}),fetch(`${API_BASE}/api/trader/orders?status=pending`,{headers:{Authorization:`Bearer ${token}`}})]);
        if(response.ok&&orders.ok){const [positions,pending]=await Promise.all([response.json(),orders.json()]);if(active&&localStorage.getItem('trader_token')===token)setApiPositions([...positions,...pending]);}
      } catch (error) {
        console.error('Failed to fetch positions:', error);
      }
    };
    
    fetchPositions();
    const interval = setInterval(fetchPositions, 5000);
    window.addEventListener('trader-data-changed',fetchPositions);
    return () => {active=false;clearInterval(interval);window.removeEventListener('trader-data-changed',fetchPositions);};
  }, []);
  
  // Also request positions via WebSocket when connected
  useEffect(() => {
    if (isConnected && requestPositions) {
      requestPositions();
    }
  }, [isConnected, requestPositions]);
  
  const positions = apiPositions;

  // Language options
  const languageOptions = [
    { code: 'en', flag: '🇬🇧', name: 'English' },
    { code: 'tr', flag: '🇹🇷', name: 'Türkçe' },
  ];

  const currentLangOption = languageOptions.find(l => l.code === language) || languageOptions[0];

  // Fetch instruments
  useEffect(() => {
    const fetchInstruments = async (silent=false) => {
      try {
        if(!silent)setLoading(true);
        const token = localStorage.getItem('trader_token');
        const response = await fetch(`${API_BASE}${token ? '/api/trader/instruments' : '/api/instruments'}?limit=1000`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Known prices for common instruments
          const knownPrices = {
            'EUR/USD': 1.0842, 'EURUSD': 1.0842,
            'GBP/USD': 1.2634, 'GBPUSD': 1.2634,
            'USD/JPY': 149.52, 'USDJPY': 149.52,
            'USD/CHF': 0.8845, 'USDCHF': 0.8845,
            'AUD/USD': 0.6523, 'AUDUSD': 0.6523,
            'USD/CAD': 1.3567, 'USDCAD': 1.3567,
            'NZD/USD': 0.5987, 'NZDUSD': 0.5987,
            'EUR/GBP': 0.8580, 'EURGBP': 0.8580,
            'EUR/JPY': 162.15, 'EURJPY': 162.15,
            'GBP/JPY': 188.90, 'GBPJPY': 188.90,
            'AUD/NZD': 1.0890, 'AUDNZD': 1.0890,
            'EUR/CHF': 0.9590, 'EURCHF': 0.9590,
            'EUR/AUD': 1.6620, 'EURAUD': 1.6620,
            'EUR/CAD': 1.4710, 'EURCAD': 1.4710,
            'USD/TRY': 38.50, 'USDTRY': 38.50,
            'EUR/TRY': 41.75, 'EURTRY': 41.75,
            'XAU/USD': 4508.00, 'XAUUSD': 4508.00,
            'XAG/USD': 28.50, 'XAGUSD': 28.50,
            'XPT/USD': 985.00, 'XPTUSD': 985.00,
            'USOIL': 82.50,
            'UKOIL': 86.30,
            'NATGAS': 2.85,
            'BTC/USD': 67500, 'BTCUSD': 67500,
            'ETH/USD': 3450, 'ETHUSD': 3450,
            'XRP/USD': 0.52, 'XRPUSD': 0.52,
            'SOL/USD': 145, 'SOLUSD': 145,
            'ADA/USD': 0.45, 'ADAUSD': 0.45,
            'DOGE/USD': 0.12, 'DOGEUSD': 0.12,
            'US100': 18500,
            'US500': 5200,
            'US30': 39500,
            'GER40': 18200,
            'UK100': 8150,
            'FRA40': 7950,
            'JP225': 38500,
            'HK50': 17800,
            'AU200': 7850,
            // BIST Indices (TRY)
            'XU100': 10250.0, 'XU030': 10850.0, 'XUBANK': 4250.0, 'XUSIN': 8750.0,
            // Major BIST stocks (TRY)
            'GARAN': 85.50, 'AKBNK': 62.30, 'ISCTR': 18.75, 'VAKBN': 24.60,
            'THYAO': 315.00, 'EREGL': 58.40, 'KCHOL': 195.00, 'SAHOL': 78.50,
            'TUPRS': 185.00, 'ASELS': 95.00, 'SISE': 52.80, 'TCELL': 88.50,
            'BIMAS': 465.00, 'FROTO': 1250.00, 'TOASO': 385.00, 'ARCLK': 195.00,
            'PGSUS': 875.00, 'HALKB': 22.50, 'YKBNK': 32.40, 'TTKOM': 48.60,
          };
          
          const transformed = data.map(inst => {
            const symbol = inst.symbol || '';
            const cleanSymbol = symbol.replace('/', '').replace('.IS', '').toUpperCase();
            const category = inst.category || inst.type || 'stocks';
            const isBIST = category === 'bist' || inst.tradingview_symbol?.startsWith('BIST:') || symbol.endsWith('.IS');
            
            // Get base price from known prices or calculate based on category
            let basePrice = knownPrices[symbol] || knownPrices[cleanSymbol] || null;
            
            if (basePrice === null) {
              if (category === 'forex') {
                // Forex pairs typically trade around 1.x for majors, 100+ for JPY pairs
                if (symbol.includes('JPY') || symbol.includes('TRY')) {
                  basePrice = symbol.includes('TRY') ? 35 + Math.random() * 10 : 140 + Math.random() * 20;
                } else {
                  basePrice = 0.8 + Math.random() * 0.6; // 0.8 to 1.4
                }
              } else if (category === 'crypto') {
                basePrice = 100 + Math.random() * 5000;
              } else if (category === 'commodities') {
                basePrice = 50 + Math.random() * 50;
              } else if (category === 'indices') {
                basePrice = 5000 + Math.random() * 15000;
              } else if (isBIST) {
                // Turkish stocks - TRY denominated, typically 20-500 TRY range
                basePrice = 50 + Math.random() * 200;
              } else {
                // General stocks
                basePrice = 50 + Math.random() * 150;
              }
            }
            
            // Calculate spread based on instrument type
            let spreadPercent = 0.0003; // Default 3 pips
            if (category === 'crypto') spreadPercent = 0.005;
            else if (category === 'indices') spreadPercent = 0.0005;
            else if (category === 'commodities') spreadPercent = 0.001;
            else if (category === 'bist') spreadPercent = 0.002;
            
            const spread = basePrice * spreadPercent;
            
            return {
              ...inst,
              id: inst.id,
              symbol: inst.symbol,
              name: inst.name,
              bid: 0,
              ask: 0,
              change: 0,
              category: category,
              tradingview_symbol: inst.tradingview_symbol,
            };
          });
          
          setInstruments(transformed);
        }
      } catch (error) {
        console.error('Failed to fetch instruments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInstruments();
    return watchLiveRefresh(()=>fetchInstruments(true));
  }, []);

  const toggleFavorite = useCallback((symbol, e) => {
    e?.stopPropagation();
    setFavorites(prev => 
      prev.includes(symbol) ? prev.filter(s => s !== symbol) : [...prev, symbol]
    );
  }, []);

  const formatPrice = useCallback((price, symbol) => {
    if (!price || isNaN(price)) return '0.00';
    if (price > 1000) return price.toFixed(2);
    if (price > 10) return price.toFixed(2);
    return price.toFixed(5);
  }, []);

  const handleInstrumentClick = (inst) => {
    setSelectedInstrument(inst);
    setShowInstrumentDetail(true);
  };

  const orderKey=useRef(null);
  const handleOpenPosition = async (orderData) => {
    try {
      const token = localStorage.getItem('trader_token');
      const response = await fetch(`${API_BASE}/api/trader/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(orderIntent(orderKey,{
          symbol: orderData.instrument.symbol,
          side: orderData.side,
          volume: Number(orderData.size),
          type: orderData.orderType || 'market',
          price: orderData.pendingPrice,
          stop_loss: orderData.stopLoss,
          take_profit: orderData.takeProfit,
        })),
      });
      
      if (response.ok) {
        toast.success(tx(`${orderData.side.toUpperCase()} ${orderData.size} ${orderData.instrument.symbol} ${orderData.orderType==='pending'?'pending':'executed'}`));
        orderKey.current=null;
        window.dispatchEvent(new Event('trader-data-changed'));
        setShowInstrumentDetail(false);
        return true;
      } else {
        const error = await response.json();
        toast.error(tx(Array.isArray(error.detail)?error.detail.map(item=>item.msg).join('; '):error.detail || 'Order failed'));
        return false;
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error(tx('Failed to place order'));
      return false;
    }
  };

  const tabs = [
    { id: 'markets', icon: ArrowUpDown, label: language === 'tr' ? 'Fiyatlar' : 'Quotes' },
    { id: 'chart', icon: CandlestickChart, label: language === 'tr' ? 'Grafik' : 'Chart' },
    { id: 'positions', icon: BarChart3, label: language === 'tr' ? 'İşlemler' : 'Trade' },
    { id: 'history', icon: History, label: language === 'tr' ? 'Geçmiş' : 'History' },
    { id: 'menu', icon: Settings, label: 'Settings' },
  ];

  const chartInstrument = useMemo(() => {
    const raw = instruments.find(inst => inst.symbol === selectedInstrument?.symbol) || 
                instruments.find(inst => inst.symbol === 'EUR/USD') || 
                instruments[0];
    if (!raw) return null;
    const live = scopedQuote(raw, prices[raw.symbol]);
    return live ? { ...raw, bid: live.bid, ask: live.ask, change: live.change_pct || raw.change } : raw;
  }, [instruments, selectedInstrument?.symbol, prices]);

  // Keep WebSocket subscribed for active tab (chart or positions)
  useEffect(() => {
    if (activeTab === 'chart' && chartInstrument?.symbol && typeof replaceSubscriptions === 'function') {
      const needed = [chartInstrument.symbol];
      positions.forEach(p => { if (p.symbol && !needed.includes(p.symbol)) needed.push(p.symbol); });
      replaceSubscriptions(needed);
    } else if ((activeTab === 'positions' || activeTab === 'history') && typeof replaceSubscriptions === 'function') {
      const needed = positions.map(p => p.symbol).filter(Boolean);
      if (needed.length > 0 && typeof replaceSubscriptions === 'function') {
        replaceSubscriptions(needed);
      }
    }
  }, [activeTab, chartInstrument?.symbol, positions, replaceSubscriptions]);

  const accountId = user?.account_id || user?.id?.slice(0, 8).toUpperCase() || 'N/A';
  const currency = user?.currency || 'USD';
  const backToMenu = () => { setCurrentView(null); setActiveTab('menu'); setShowSidebar(true); };
  const serviceView = content => <div className="mt5-mobile mt5-service" data-theme={isDark ? 'dark' : 'light'}>{content}</div>;

  // Render full-screen views from sidebar navigation
  if (currentView === 'deposit') {
    return serviceView(<div className="tr-deposit-mobile-shell"><header className="tr-deposit-mobile-back"><button data-testid="mobile-deposit-back" onClick={backToMenu}><ChevronLeft size={20}/>{tx(language==='tr'?'Geri':'Back')}</button></header><TraderConnection/><DepositWorkspace/></div>);
  }
  if (currentView === 'withdraw') {
    return serviceView(<div className="tr-deposit-mobile-shell"><header className="tr-deposit-mobile-back"><button data-testid="mobile-withdraw-back" onClick={backToMenu}><ChevronLeft size={20}/>{tx(language==='tr'?'Geri':'Back')}</button></header><TraderConnection/><WithdrawalWorkspace/></div>);
  }
  if (currentView === 'calendar') {
    return serviceView(<MobileCalendarView onBack={backToMenu} />);
  }
  if (currentView === 'settings') {
    return serviceView(<MobileSettingsView onBack={backToMenu} />);
  }
  if (currentView === 'documents') {
    return serviceView(<MobileDocumentsView onBack={backToMenu} />);
  }

  return (
    <div data-testid="mobile-terminal-shell" data-theme={isDark ? 'dark' : 'light'} className={`mt5-mobile h-screen flex flex-col ${isDark ? 'bg-[#0a0e17]' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`mt5-mobile-header px-4 py-3 flex items-center justify-between ${isDark ? 'bg-[#0f1724]' : 'bg-white'}`}>
        {/* Profile Avatar - Opens Sidebar */}
        <button 
          onClick={() => { setActiveTab('menu'); setShowSidebar(true); }}
          data-testid="mobile-user-menu-toggle"
          className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
            isDark ? 'border-cyan-500 text-cyan-500' : 'border-blue-500 text-blue-500'
          }`}
        >
          <Menu size={21}/>
        </button>

        {/* Account Info - Shows Account ID */}
        <button
          onClick={() => setShowAccountDropdown(!showAccountDropdown)}
          data-testid="mobile-account-switch-toggle"
          className={`px-3 py-1.5 rounded-lg border flex flex-col items-center ${
            isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
          }`}
        >
          <span data-testid="mobile-current-view" className="mt5-mobile-title">{tx(tabs.find(tab => tab.id === activeTab)?.label || (activeTab === 'funds' ? 'Funds' : 'Home'))}</span>
          <div className="flex items-center gap-1.5">
            <span className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{accountId}</span>
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>| {currency}</span>
            <ChevronDown size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          </div>
        </button>

        {/* Notifications */}
        <button data-testid="mobile-notifications-toggle" aria-label={tx("Notifications")} onClick={()=>setShowCustomerNotifications(!showCustomerNotifications)} className={`relative p-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          <Bell size={24} />
          <NotificationBadge id="mobile-notification-unread-badge"/>
        </button>
      </header>
      <AccessApprovalNotice/>
      <TraderConnection/>
      {showAccountDropdown&&<div className={`mx-4 border rounded max-h-60 overflow-y-auto ${isDark?'bg-gray-900 border-gray-700':'bg-white border-gray-200'}`}><AccountSwitcher onClose={()=>setShowAccountDropdown(false)}/></div>}
      {showCustomerNotifications&&<><button type="button" className="mobile-inbox-backdrop" data-testid="mobile-inbox-dismiss" aria-label={tx("Close notifications")} onClick={()=>setShowCustomerNotifications(false)}/><div data-testid="mobile-inbox-panel" className={`mobile-inbox-panel border ${isDark?'bg-gray-900 border-gray-700':'bg-white border-gray-200'}`}><CustomerNotifications/></div></>}

      {/* Content Area */}
      <main className="mt5-mobile-content flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <>
            {activeTab === 'home' && (
              <MobileHomeView
                instruments={instruments}
                favorites={favorites}
                toggleFavorite={toggleFavorite}
                onSelectInstrument={handleInstrumentClick}
                onInstrumentClick={handleInstrumentClick}
                formatPrice={formatPrice}
                user={user}
                isDark={isDark}
                t={t}
              />
            )}
            {activeTab === 'markets' && (
              <MobileMarketsView
                instruments={instruments}
                favorites={favorites}
                toggleFavorite={toggleFavorite}
                onSelectInstrument={handleInstrumentClick}
                onInstrumentClick={handleInstrumentClick}
                formatPrice={formatPrice}
                isDark={isDark}
                t={t}
                selectedSymbol={selectedInstrument?.symbol}
                positionSymbols={positions.map(p => p.symbol)}
              />
            )}
            {activeTab === 'chart' && chartInstrument && (
              <MobileInstrumentDetail
                embedded
                instrument={chartInstrument}
                instruments={instruments}
                onSelectInstrument={handleInstrumentClick}
                onClose={() => setActiveTab('markets')}
                onOpenPosition={handleOpenPosition}
                formatPrice={formatPrice}
                isFavorite={favorites.includes(chartInstrument.symbol)}
                toggleFavorite={toggleFavorite}
              />
            )}
            {(activeTab === 'positions' || activeTab === 'history') && (
              <MobilePositionsView
                key={activeTab}
                historyOnly={activeTab === 'history'}
                user={user}
                positions={positions}
                isDark={isDark}
                t={t}
              />
            )}
            {activeTab === 'funds' && (
              <MobileFundsView
                user={user}
                positions={positions}
                isDark={isDark}
                t={t}
              />
            )}
          </>
        )}
      </main>

      {/* Bottom Navigation - with extra padding for Emergent badge */}
      <nav data-testid="mobile-terminal-navigation" className={`mt5-mobile-navigation flex items-center justify-around py-3 border-t safe-area-inset-bottom ${
        isDark ? 'bg-[#0f1724] border-gray-800' : 'bg-white border-gray-200'
      }`}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setShowSidebar(tab.id === 'menu');
              }}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all active:scale-95 ${
                isActive
                  ? isDark ? 'text-cyan-400 bg-cyan-500/10' : 'text-cyan-600 bg-cyan-50'
                  : isDark ? 'text-gray-500 active:text-gray-300' : 'text-gray-400 active:text-gray-600'
              }`}
              data-testid={`mobile-nav-${tab.id}`}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon size={22} strokeWidth={isActive ? 2.5 : 1.5} />
              <span className={`text-[10px] font-medium ${isActive ? 'font-bold' : ''}`}>{tx(tab.label)}</span>
            </button>
          );
        })}
      </nav>

      {/* Order Modal */}
      <NewOrderModal
        isOpen={orderModalOpen}
        onClose={() => setOrderModalOpen(false)}
        instrument={selectedInstrument}
      />

      {/* Mobile Instrument Detail View */}
      {showInstrumentDetail && selectedInstrument && (
        <MobileInstrumentDetail
          instrument={selectedInstrument}
          instruments={instruments}
          onSelectInstrument={handleInstrumentClick}
          onClose={() => setShowInstrumentDetail(false)}
          onOpenPosition={handleOpenPosition}
          formatPrice={formatPrice}
          isFavorite={favorites.includes(selectedInstrument.symbol)}
          toggleFavorite={toggleFavorite}
        />
      )}

      {/* Mobile Sidebar Menu */}
      {showSidebar && (
        <div className="mt5-mobile-menu fixed inset-0 z-[200] flex" data-testid="mobile-trader-sidebar" role="region" aria-label={tx('Menu')}>
          {/* Sidebar Panel */}
          <div 
            className={`w-[85%] max-w-[320px] h-full flex flex-col ${
              isDark ? 'bg-[#0f1724]' : 'bg-white'
            }`}
            style={{ animation: 'slideInLeft 0.3s ease-out' }}
          >
            {/* Header with Logo and Language */}
            <div className={`px-5 py-4 flex items-center justify-between border-b ${
              isDark ? 'border-gray-800' : 'border-gray-200'
            }`}>
              {/* Logo/Brand */}
              <strong className="mt5-mobile-menu-title">{tx('Settings')}</strong>
              
              {/* Language Selector */}
              <div className="relative">
                <button 
                  onClick={() => setShowLanguageSelector(!showLanguageSelector)}
                  data-testid="mobile-language-toggle"
                  aria-label={tx('Language')}
                  className={`px-3 py-2 rounded-full border flex items-center gap-2 ${
                    isDark ? 'border-gray-700 text-white' : 'border-gray-200 text-gray-900'
                  }`}
                >
                  <span className="text-lg">{currentLangOption.flag}</span>
                  <ChevronDown size={14} className={`transition-transform ${showLanguageSelector ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Language Dropdown */}
                {showLanguageSelector && (
                  <div className={`absolute top-full right-0 mt-2 w-40 rounded-xl shadow-xl border z-[100] ${
                    isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}>
                    {languageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        data-testid={`mobile-language-${lang.code}`}
                        onClick={() => {
                          setLanguage(lang.code);
                          setShowLanguageSelector(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm ${
                          language === lang.code
                            ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-50 text-cyan-600'
                            : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                        } ${lang.code === languageOptions[0].code ? 'rounded-t-xl' : ''} ${lang.code === languageOptions[languageOptions.length - 1].code ? 'rounded-b-xl' : ''}`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto py-4" style={{ WebkitOverflowScrolling: 'touch' }}>
              <button className="mt5-mobile-account-card" data-testid="mobile-menu-account" onClick={() => { setShowSidebar(false); setActiveTab('markets'); setShowAccountDropdown(true); }}>
                <strong>{user?.full_name || tx('Trader')}</strong><span>{accountId} · {currency}</span><ChevronRight size={18}/>
              </button>
              {/* Home */}
              <button 
                onClick={() => { setActiveTab('home'); setShowSidebar(false); }}
                data-testid="mobile-menu-home"
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Home size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Home")}</span>
              </button>

              {/* Funds */}
              <button 
                onClick={() => { setActiveTab('funds'); setShowSidebar(false); }}
                data-testid="mobile-menu-funds"
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Banknote size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Funds")}</span>
              </button>

              {/* Deposit */}
              <button 
                onClick={() => { setCurrentView('deposit'); setShowSidebar(false); }}
                data-testid="mobile-menu-deposit"
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <ArrowDownCircle size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Deposit")}</span>
              </button>

              {/* Withdraw */}
              <button 
                onClick={() => { setCurrentView('withdraw'); setShowSidebar(false); }}
                data-testid="mobile-menu-withdraw"
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <ArrowUpCircle size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Withdraw")}</span>
              </button>

              {/* Economic Calendar */}
              <button 
                onClick={() => { setCurrentView('calendar'); setShowSidebar(false); }}
                data-testid="mobile-menu-calendar"
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Calendar size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Economic Calendar")}</span>
              </button>

              {/* Help Center */}
              <button 
                onClick={() => navigate('/trader/help')}
                data-testid="mobile-menu-help"
                className={`w-full flex items-center justify-between px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-4">
                  <HelpCircle size={22} strokeWidth={1.5} />
                  <span className="text-base font-medium">{tx("Help Center")}</span>
                </div>
                <ChevronRight size={18}/>
              </button>
              

              {/* Personal Settings */}
              <button 
                onClick={() => { setCurrentView('settings'); setShowSidebar(false); }}
                data-testid="mobile-menu-settings"
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Settings size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Personal Settings")}</span>
              </button>

              {/* Upload Documents */}
              <button 
                onClick={() => { setCurrentView('documents'); setShowSidebar(false); }}
                data-testid="mobile-menu-documents"
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Upload size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Upload documents")}</span>
              </button>

              {/* Install App / Add to Home Screen */}
              {!isInstalled && (
                <button 
                  onClick={() => { setShowSidebar(false); promptInstall(); }}
                  data-testid="mobile-menu-install"
                  className={`w-full flex items-center gap-4 px-5 py-4 bg-gradient-to-r from-cyan-500/10 to-transparent border-l-2 border-cyan-400 ${
                    isDark ? 'text-cyan-400 hover:bg-cyan-500/20' : 'text-cyan-600 hover:bg-cyan-50'
                  }`}
                >
                  <Smartphone size={22} strokeWidth={1.8} className="text-cyan-400" />
                  <div className="text-left">
                    <span className="text-base font-semibold block">{tx(language === 'tr' ? 'Uygulamayı Yükle' : 'Install App')}</span>
                    <span className={`text-[11px] block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {tx(language === 'tr' ? 'Ana Ekrana & Çekmeceye Ekle' : 'Add to Home Screen & App Tray')}
                    </span>
                  </div>
                </button>
              )}

              {/* Divider */}
              <div className={`my-4 mx-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`} />

              {/* Theme Toggle */}
              <button 
                onClick={() => { toggleTheme && toggleTheme(); }}
                data-testid="mobile-menu-theme"
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                {isDark ? <Sun size={22} strokeWidth={1.5} /> : <Moon size={22} strokeWidth={1.5} />}
                <span className="text-base font-medium">{tx(isDark ? 'Light Mode' : 'Dark Mode')}</span>
              </button>

              {/* Log Out */}
              <button 
                data-testid="mobile-menu-logout"
                onClick={() => { 
                  if (logout) logout();
                  setShowSidebar(false);
                }}
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <LogOut size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Log out")}</span>
              </button>
            </div>

          </div>

          {/* Overlay with Close Button */}
          <div 
            className="flex-1 bg-black/50 relative"
            onClick={() => setShowSidebar(false)}
          >
            <button 
              onClick={() => setShowSidebar(false)}
              data-testid="mobile-sidebar-close"
              aria-label={tx('Close')}
              className="absolute top-4 right-4 p-2 text-white"
            >
              <X size={28} />
            </button>
          </div>
        </div>
      )}

      {/* PWA Install Modal */}
      <PWAInstallModal
        isOpen={showIOSModal}
        onClose={() => setShowIOSModal(false)}
        isIOS={isIOS}
      />

      {/* Sidebar Animation Styles */}
      <style>{`
        @keyframes slideInLeft {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default MobileTraderLayout;
